import { ui } from '../../config/uiSizes';

export type Schedule = {
  days: string[]; // e.g. ['MON','TUE']
  hour: number | null; // 1-12
  minute: number | null; // 0-59
  ampm: "AM" | "PM";
};

interface Props {
  value: Schedule;
  onChange: (next: Schedule) => void;
  className?: string;
  showTime?: boolean;
}

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function toggleDay(days: string[], day: string) {
  return days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
}

export default function SchedulePicker({ value, onChange, className = "", showTime = true }: Props) {
  const { days, hour, minute, ampm } = value;

  const setDays = (day: string) => onChange({ ...value, days: toggleDay(days, day) });

  const setHour = (h: number | null) => onChange({ ...value, hour: h });
  const setMinute = (m: number | null) => onChange({ ...value, minute: m });
  const setAmPm = (a: "AM" | "PM") => onChange({ ...value, ampm: a });

  return (
    <div className={`flex items-center ${ui.gap.lg} ${className}`}>
      {/* Days */}
      <div className={`flex items-center ${ui.gap.dayPicker}`}>

        {DAYS.map((d) => {
          const selected = days.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`${ui.widthAndHeight.dayButton} flex items-center justify-center ${ui.text.dayButtonLabel} font-bold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 ${selected
                  ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg scale-105 dark:from-blue-700 dark:to-blue-800"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                }`}
              aria-pressed={selected}
              title={d}
            >
              {d.slice(0, 3)}
            </button>
          );
        })}
      </div>

      {/* Time picker (optional) */}
      {showTime && (
        <div className={`flex items-center ${ui.gap.sm}`}>
          <select
            aria-label="Hour"
            value={hour ?? ""}
            onChange={(e) => setHour(e.target.value === "" ? null : Number(e.target.value))}
            className={`${ui.widthAndHeight.selectTime} text-center rounded-md border bg-white dark:bg-gray-900`}
          >
            <option value="">HH</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>

          <select
            aria-label="Minute"
            value={minute ?? ""}
            onChange={(e) => setMinute(e.target.value === "" ? null : Number(e.target.value))}
            className={`${ui.widthAndHeight.selectTime} text-center rounded-md border bg-white dark:bg-gray-900`}
          >
            <option value="">MM</option>
            {Array.from({ length: 60 }, (_, i) => i).map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, "0")}
              </option>
            ))}
          </select>

          <select
            aria-label="AM/PM"
            value={ampm}
            onChange={(e) => setAmPm(e.target.value as "AM" | "PM")}
            className={`${ui.widthAndHeight.selectAmPm} text-center rounded-md border bg-white dark:bg-gray-900`}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      )}
    </div>
  );
}
